import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Seo from "components/Seo";
import CustomerLayout from "./customerLayout";
import Input from "../../-components/common/Input";
import Button from "../../-components/common/Button";
import { useMorningAuthStore } from "../../../../../store/useMorningAuthStore";
import Radio from "../../-components/common/Radio";
import SelectBox from "../../-components/common/SelectBox";
import { useGetDepartments } from "../../../../../hooks/morning/useGetDept";
import DayPickerInput from "../../-components/common/DayPickerInput";
import { differenceInCalendarDays, format, subDays } from "date-fns";
import { TrackingRequest, TrackingResponse, TrackingSummary, usePostTrackingList } from "../../../../../hooks/morning/usePostTrackingList";
import TrackingList from "../../-components/TrackingList";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import filter from "assets/morning/icon/filter.svg";
import Spinner from "components/Spinner";
import { useAppContext } from "context/AppContext";
import { useIsDevelopment } from "hooks/utils/useIsDevelopment";

type SearchFormProps = {
    page: number;
    pageSize: number;
    blNo: string;
    status: number | string;
    sender: string;
    receiver: string;
    startDate: Date | undefined;
    endDate: Date | undefined;
    send: string | number;
    department: string;
    payKind: string; // 지불구분 (요약 숫자 클릭)
    excludeCancel: boolean; // 접수취소 제외 (요약 숫자 클릭)
};

const initialSearchForm = {
    page: 1, // getPage
    pageSize: 10, // getPageSize
    blNo: "", // bl_no
    status: 0, // o_kind: '',
    sender: "", // s_a_name: '',
    receiver: "", //s_b_contact: '',
    startDate: undefined, //s _date
    endDate: undefined, // e_date
    send: "", //s_kind: '',
    department: "all", //s_p_dept: '',
    payKind: "", // pay_kind
    excludeCancel: false, // exclude_cancel
};

const deliveryStatusOptions = [
    { label: "전체", value: 0 },
    { label: "오더접수", value: 1 },
    { label: "픽업예약", value: 2 },
    { label: "픽업완료", value: 3 },
    { label: "배송중", value: 4 },
    { label: "배송완료", value: 5 },
    { label: "접수취소", value: 6 },
];
const SendStatusOptions = [
    { label: "당사픽업접수", value: "H" },
    { label: "외부업체픽업접수", value: "A" },
];

const formatDate = (date?: Date) => {
    return date ? format(date, "yyyy-MM-dd") : "";
};

export const parseDateString = (str?: string): Date | undefined => {
    if (!str) return;
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
};

type SummaryFilter = {
    status: number | string;
    payKind: string;
    excludeCancel: boolean;
};

const TrackingSummaryBox = ({
    summary,
    filter,
    onFilter,
    onReset,
}: {
    summary: TrackingSummary;
    filter: SummaryFilter;
    onFilter: (patch: Partial<SummaryFilter>) => void;
    onReset: () => void;
}) => {
    const status = String(filter.status ?? 0);
    const isAll = status === "0" && !filter.payKind && !filter.excludeCancel;
    const isCancel = status === "6" && !filter.payKind && !filter.excludeCancel;
    const isNet = filter.excludeCancel && !filter.payKind;
    const isFiltered = !isAll;

    // 숫자 버튼: hover 시 밑줄, 현재 적용된 필터는 밑줄 유지
    const numberButton = (label: string, value: number, unit: string, active: boolean, onClick: () => void, key?: string) => (
        <button key={key} type="button" onClick={onClick} className="group whitespace-nowrap">
            {label}{" "}
            <b className={`text-new group-hover:underline ${active ? "underline" : ""}`}>{value}</b>
            {unit}
        </button>
    );

    return (
        <div className="shrink-0 max-w-full border border-new rounded-lg bg-white px-4 py-3 text-[15px] text-[#1b1c1e]">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-semibold">
                {numberButton("접수", summary.accept_count, "건", isAll, () =>
                    onFilter({ status: 0, payKind: "", excludeCancel: false }),
                )}
                {numberButton("취소", summary.cancel_count, "건", isCancel, () =>
                    onFilter({ status: 6, payKind: "", excludeCancel: false }),
                )}
                {numberButton("총접수", summary.net_count, "건", isNet, () =>
                    onFilter({ status: 0, payKind: "", excludeCancel: true }),
                )}
                <span className="whitespace-nowrap">
                    총수량 <b className="text-new">{summary.total_qty}</b>개
                </span>
                {isFiltered && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="ml-auto shrink-0 font-[500] text-[13px] text-[#5A5C63] border border-[#E1E2E4] rounded px-2 py-[3px] hover:bg-[#f7f7f8]"
                    >
                        초기화
                    </button>
                )}
            </div>
            {summary.payList.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#E1E2E4]">
                    <p className="text-[13px] text-[#8b8d94] mb-1">총접수 {summary.net_count}건 기준</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-[#5A5C63]">
                        {summary.payList.map(pay =>
                            numberButton(
                                pay.pay_name,
                                pay.count,
                                "건",
                                filter.payKind === pay.pay_code,
                                () => onFilter({ status: 0, payKind: pay.pay_code, excludeCancel: true }),
                                pay.pay_code,
                            ),
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Tracking = ({ token, isApp }: { token?: string | null; isApp: boolean }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [detailPop, setDetailPop] = useState<boolean>(false);
    const [trackingResult, setTrackingResult] = useState<TrackingResponse>();
    const [initialized, setInitialized] = useState(false);
    const [searchForm, setSearchForm] = useState<SearchFormProps>(initialSearchForm);
    const [searchFilter, setSearchFilter] = useState<TrackingRequest>();
    const [selectedRange, setSelectedRange] = useState<number | null>(null);
    // 접수 현황 요약: 날짜를 지정하고 검색 버튼을 눌렀을 때만 노출
    const [showSummary, setShowSummary] = useState(false);

    const isMaster = location.pathname.includes("master");
    const c_code = useMorningAuthStore(state => state.c_code ?? "");
    const isLoggedInIlshin = c_code === "0500001";
    const responsive700 = useMediaQuery(700);

    // const filterParams = useMemo(
    //     () => ({
    //         bl_no: blNo,
    //         o_kind: status === 0 ? "" : status.toString(),
    //         s_date: startDate ? format(startDate, "yyyy-MM-dd") : "",
    //         e_date: endDate ? format(endDate, "yyyy-MM-dd") : "",
    //         s_kind: send.toString(),
    //         s_a_name: sender,
    //         s_b_contact: receiver,
    //         s_p_dept: department === "all" ? "" : department,
    //     }),
    //     [blNo, status, startDate, endDate, send, sender, receiver, department],
    // );

    const { data: deptData } = useGetDepartments(
        { master: isMaster },
        // { enabled: !!token }
    );
    const { mutate: trackingList, isPending } = usePostTrackingList(token);

    useEffect(() => {
        if (initialized) return;

        const urlParams = new URLSearchParams(window.location.search);

        const startDate = parseDateString(urlParams.get("startDate") ?? undefined);
        const endDate = parseDateString(urlParams.get("endDate") ?? undefined);

        // ✅ selectedRange 자동 설정
        if (startDate && endDate) {
            const diffDays = differenceInCalendarDays(endDate, startDate);

            const predefinedRanges = [7, 30, 182, 365];
            if (predefinedRanges.includes(diffDays)) {
                setSelectedRange(diffDays);
            } else {
                setSelectedRange(null);
            }
        } else {
            setSelectedRange(null);
        }

        const params = {
            page: Number(urlParams.get("page") ?? 1),
            pageSize: 10,
            blNo: urlParams.get("blNo") ?? "",
            status: urlParams.get("status") ?? 0,
            sender: urlParams.get("sender") ?? "",
            receiver: urlParams.get("receiver") ?? "",
            startDate,
            endDate,
            send: urlParams.get("send") ?? "",
            department: urlParams.get("department") ?? "all",
            payKind: urlParams.get("payKind") ?? "",
            excludeCancel: urlParams.get("excludeCancel") === "Y",
        };

        // 새로고침 복원: 상세조회 조건이 URL 에 남아 있으면 패널을 펼쳐 적용된 조건을 보여준다.
        const hasDetailCondition =
            String(params.status) !== "0" ||
            !!params.sender ||
            !!params.receiver ||
            !!params.send ||
            params.department !== "all" ||
            !!params.payKind ||
            params.excludeCancel ||
            !!(startDate && endDate);

        if (hasDetailCondition) setDetailPop(true);
        // 요약은 기간 검색 결과에만 딸린 값이므로 날짜가 있을 때만 되살린다.
        if (startDate && endDate) setShowSummary(true);

        setSearchForm(params);
        fetchTrackingList(params, false);
        setInitialized(true);
    }, [initialized]);

    const fetchTrackingList = (params?: SearchFormProps, syncToUrl = true) => {
        // ✅ params 우선 처리
        // const source = params ?? searchForm;

        // 부분적으로만 override 되는 경우를 생각하여 병합 방식으로 수정
        const source = {
            ...searchForm,
            ...params,
        };

        const page = source.page ?? 1;
        const pageSize = source.pageSize ?? 10;
        const blNo = source.blNo ?? "";
        const status = source.status ?? 0;
        const sender = source.sender ?? "";
        const receiver = source.receiver ?? "";
        const startDate = source.startDate ?? null;
        const endDate = source.endDate ?? null;
        const send = source.send ?? "";
        const department = params?.department ?? "all";
        const payKind = source.payKind ?? "";
        const excludeCancel = source.excludeCancel ?? false;

        const o_kind = status === 0 || status === "0" ? "" : status.toString();
        const s_date = startDate ? formatDate(startDate) : "";
        const e_date = endDate ? formatDate(endDate) : "";
        const s_kind = send.toString();
        const s_p_dept = department === "all" ? "" : department;

        const newFilter: TrackingRequest = {
            getPage: page,
            getPageSize: pageSize,
            bl_no: blNo,
            o_kind,
            s_a_name: sender,
            s_b_contact: receiver,
            s_date,
            e_date,
            s_kind,
            s_p_dept,
            pay_kind: payKind,
            exclude_cancel: excludeCancel ? "Y" : "",
        };

        // URLParams 설정
        const newParams = new URLSearchParams(window.location.search);
        const setOrDeleteParam = (key: string, value: any) => {
            if (value !== undefined && value !== null && value !== "") {
                newParams.set(key, String(value));
            } else {
                newParams.delete(key);
            }
        };

        setOrDeleteParam("page", page);
        setOrDeleteParam("pageSize", pageSize);
        setOrDeleteParam("blNo", blNo);
        setOrDeleteParam("status", status);
        setOrDeleteParam("sender", sender);
        setOrDeleteParam("receiver", receiver);
        setOrDeleteParam("startDate", s_date);
        setOrDeleteParam("endDate", e_date);
        setOrDeleteParam("send", s_kind);
        setOrDeleteParam("department", department);
        setOrDeleteParam("payKind", payKind);
        setOrDeleteParam("excludeCancel", excludeCancel ? "Y" : "");

        // URL 강제 갱신 (히스토리 푸시)
        if (syncToUrl) {
            const newUrl = `${window.location.pathname}?${newParams.toString()}`;
            window.history.pushState(null, "", newUrl);
        }

        setSearchFilter(newFilter);

        // setPage(newFilter.getPage);
        trackingList(newFilter, {
            onSuccess: res => {
                if (res) setTrackingResult(res);
            },
            onError: (error: any) => {
                const message = error.response.data.message;
                alert("error" + message);
            },
        });
    };

    // 요약 숫자 클릭 시 목록만 필터링한다. 요약 수치는 서버에서 기간 전체 기준으로 계산되어 그대로 유지된다.
    const handleSummaryFilter = (patch: Partial<SummaryFilter>) => {
        setSearchForm(prev => {
            const next: SearchFormProps = { ...prev, ...patch, page: 1 };
            fetchTrackingList(next);
            return next;
        });
    };

    const handleRangeClick = (days: number) => {
        setSelectedRange(days);
        setShowSummary(false);
        const today = new Date();
        const from = subDays(today, days);
        setSearchForm(prev => ({ ...prev, startDate: from, endDate: today }));
    };

    const handleReset = () => {
        setSelectedRange(null);
        setShowSummary(false);
        setSearchForm(initialSearchForm);
        fetchTrackingList(initialSearchForm, true); // true로 넘겨야 URL까지 반영됨
    };

    // const handleTrackingList = (params?: TrackingRequest) => {
    //     const newFilter: TrackingRequest = {
    //         getPage: params?.getPage ?? page,
    //         getPageSize: params?.getPageSize ?? 10,
    //         bl_no: params?.bl_no ?? blNo,
    //         o_kind: params?.o_kind ?? (status === 0 ? "" : status.toString()),
    //         s_a_name: params?.s_a_name ?? sender,
    //         s_b_contact: params?.s_b_contact ?? receiver,
    //         s_date: params?.s_date ?? formatDate(startDate),
    //         e_date: params?.e_date ?? formatDate(endDate),
    //         s_kind: params?.s_kind ?? send.toString(),
    //         s_p_dept: params?.s_p_dept ?? (department === "all" ? "" : department),
    //     };

    //     // setSearchFilter(newFilter);
    //     // setPage(newFilter.getPage);
    //     trackingList(newFilter, {
    //         onSuccess: res => {
    //             setTrackingResult(res);
    //         },
    //         onError: (error: any) => {
    //             const message = error.response.data.message;
    //             alert(message);
    //         },
    //     });
    // };

    // useEffect(() => {
    //     trackingList(
    //         {
    //             getPage: page,
    //             getPageSize: 10,
    //             bl_no: blNo,
    //             o_kind: status === 0 ? "" : status.toString(),
    //             s_a_name: sender,
    //             s_b_contact: receiver,
    //             s_date: startDate ? format(startDate, "yyyy-MM-dd") : "",
    //             e_date: endDate ? format(endDate, "yyyy-MM-dd") : "",
    //             s_kind: send.toString(),
    //             s_p_dept: department === "all" ? "" : department,
    //         },
    //         {
    //             onSuccess: res => {
    //                 setTrackingResult(res);
    //             },
    //         },
    //     );
    // }, [page]);

    // useEffect(() => {
    //     const params = location.state?.filter;
    //     if (params) {
    //         setPage(params.getPage);
    //         setBlNo(params.bl_no);
    //         setStatus(params.o_kind);
    //         setSender(params.s_a_name);
    //         setReceiver(params.s_b_contact);
    //         setStartDate(new Date(params.s_date));
    //         setEndDate(new Date(params.e_date));
    //         setSend(params.s_kind);
    //         setDepartment(params.s_p_dept);
    //         handleTrackingList(params);
    //     }
    // }, [location]);

    return (
        <React.Fragment>
            {isPending && <Spinner />}
            <Seo type="morning" pageTitle="운송장조회" />
            <CustomerLayout>
                <div className="flex-1 bg-[#f8f8f8] flex flex-col items-center pt-12 responsive1200:pt-[12px] responsive1200:bg-white">
                    <h2 className="hidden responsive1200:block text-[24px] text-[#333333] font-[500] ">운송장 조회</h2>
                    <div className="bg-white mt-12 w-full max-w-[1200px] px-10 py-6 rounded-2xl flex flex-col items-center mb-6 responsive1200:mt-[12px] responsive1200:mb-0 responsive1200:px-[4%]">
                        <p className="text-[#5a5c63] break-keep responsive1200:text-center">
                            B/L 번호로 운송장 현황을 조회/출력/취소 하실 수 있습니다.
                        </p>
                        <div className="w-full bg-[#f7f7f8] px-5 pt-5 pb-4 my-[10px] responsive1200:bg-white responsive1200:px-0 responsive1200:pb-0 responsive1200:mb-0">
                            <div className="flex w-full items-center gap-1 responsive1200:flex-col responsive1200:items-start">
                                <div className="flex items-center responsive700:justify-between responsive1200:w-full">
                                    <p className="text-[#1b1c1e] font-semibold text-[18px]">운송장번호</p>
                                    {c_code && responsive700 && (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => navigate(`/customer/tracking/filter${window.location.search}`)}
                                                className="border font-[500] items-center justify-center flex bg-white border-new gap-2 text-[#1b1c1e] h-[33px] min-w-[auto] px-[12px] rounded-[4px]"
                                            >
                                                {responsive700 ? (
                                                    <img src={filter} alt="" />
                                                ) : (
                                                    <div className="relative w-[14px] h-[14px] responsive700:hidden">
                                                        <span className="absolute top-1/2 left-0 w-full h-[1px] bg-new -translate-y-1/2"></span>
                                                        <span
                                                            className={`
                                                 absolute left-1/2 top-0 h-full w-[1px] bg-new -translate-x-1/2
                                                 transition-all duration-300
                                                 ${detailPop ? "opacity-0 scale-y-50" : "opacity-100 scale-y-100"}
                                             `}
                                                        ></span>
                                                    </div>
                                                )}

                                                <p className="responsive700:text-[14px]">상세조회</p>
                                            </button>
                                            {isLoggedInIlshin && (
                                                <button
                                                    onClick={() => navigate("/customer/tracking/request-quick-pickup")}
                                                    className="text-[14px] border font-[500] items-center justify-center flex bg-white border-new gap-2 text-[#1b1c1e] h-[33px] min-w-[auto] px-[12px] rounded-[4px]"
                                                >
                                                    퀵 픽업요청
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center flex-1 gap-1 responsive1200:w-full">
                                    <Input
                                        className="w-full responsive1200:[&_input]:bg-[#F7F7F8]"
                                        placeholder="(숫자9자리만 넣어주세요)(-XXX 제외)"
                                        value={searchForm.blNo}
                                        onChange={e => setSearchForm(prev => ({ ...prev, blNo: e.target.value }))}
                                        onKeyDown={(e: any) => {
                                            if (e.key === "Enter") {
                                                if (searchForm.blNo.startsWith("1")) {
                                                    navigate(`/customer/tracking/detail?bl_no=${searchForm.blNo}`);
                                                } else {
                                                    fetchTrackingList();
                                                }
                                            }
                                        }}
                                    />

                                    {c_code && !responsive700 && (
                                        <button
                                            onClick={() => {
                                                setDetailPop(!detailPop);
                                            }}
                                            className="border-2 font-[500] items-center justify-center flex text-[18px] bg-white border-new gap-2 min-w-[150px] h-[50px] rounded-lg text-[#1b1c1e]"
                                        >
                                            <div className="relative w-[14px] h-[14px]">
                                                {/* bg-[#1b1c1e] */}
                                                <span className="absolute top-1/2 left-0 w-full h-[2px] bg-new -translate-y-1/2"></span>
                                                <span
                                                    className={`
                                                absolute left-1/2 top-0 h-full w-[2px] bg-new -translate-x-1/2
                                                transition-all duration-300
                                                ${detailPop ? "opacity-0 scale-y-50" : "opacity-100 scale-y-100"}
                                            `}
                                                ></span>
                                            </div>
                                            <p>상세조회</p>
                                        </button>
                                    )}
                                </div>
                            </div>
                            {detailPop && !responsive700 && (
                                <div className="flex flex-col mt-5">
                                    <div className="border-y py-6 flex items-center">
                                        <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">운송장 상태</p>
                                        <div className=" flex items-center gap-8">
                                            <Radio
                                                gap="gap-[48px] flex-wrap responsive1200:gap-[30px] responsive1200:gap-y-[10px]"
                                                name="delivery_status"
                                                options={deliveryStatusOptions}
                                                selected={Number(searchForm.status)}
                                                onChange={(val: number | string) => setSearchForm(prev => ({ ...prev, status: val }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="border-b py-3 flex items-center">
                                        <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">접수자</p>
                                        <Input
                                            className="w-full"
                                            value={searchForm.sender}
                                            onChange={e => setSearchForm(prev => ({ ...prev, sender: e.target.value }))}
                                        />
                                    </div>
                                    <div className="border-b py-3 flex items-center">
                                        <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">도착지</p>
                                        <Input
                                            className="w-full"
                                            value={searchForm.receiver}
                                            onChange={e => setSearchForm(prev => ({ ...prev, receiver: e.target.value }))}
                                        />
                                    </div>
                                    <div className="border-b py-3 flex items-center">
                                        <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">접수일자</p>

                                        <div className="flex border font-[500] border-new rounded-lg h-[50px] mr-4 overflow-hidden">
                                            {[
                                                { label: "1주일", value: 7 },
                                                { label: "1개월", value: 30 },
                                                { label: "6개월", value: 182 },
                                                { label: "1년", value: 365 },
                                            ].map(({ label, value }, idx) => (
                                                <button
                                                    key={value}
                                                    className={`w-[82px] border-new ${idx < 3 ? "border-r" : ""
                                                        } ${selectedRange === value ? "bg-new text-white" : "text-[#1b1c1e]"}`}
                                                    onClick={() => handleRangeClick(value)}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex gap-3 relative bg-white h-[52px] px-3 border border-[#E1E2E4] rounded-lg">
                                            <DayPickerInput
                                                initialDate={searchForm.startDate}
                                                twoCalendar
                                                onDateChange={date => {
                                                    setShowSummary(false);
                                                    setSearchForm(prev => ({ ...prev, startDate: date }));
                                                }}
                                            />
                                            <DayPickerInput
                                                initialDate={searchForm.endDate}
                                                twoCalendar
                                                onDateChange={date => {
                                                    setShowSummary(false);
                                                    setSearchForm(prev => ({ ...prev, endDate: date }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="border-b py-[26px] flex items-start">
                                        <p className="min-w-[160px] shrink-0 text-[#1b1c1e] font-semibold text-[18px]">발송구분</p>
                                        {/* 라벨과 분리해 내용 영역 안에서만 줄바꿈시킨다. 요약 박스가 아랫줄로 내려가도 내용 칸 시작점에 맞춰진다. */}
                                        <div className="flex-1 flex items-center flex-wrap gap-x-8 gap-y-4">
                                            <div className="flex items-center gap-8 shrink-0">
                                                <Radio
                                                    gap="gap-20 flex-wrap gap-y-2"
                                                    name="delivery_status"
                                                    options={SendStatusOptions}
                                                    selected={searchForm.send}
                                                    onChange={(val: string | number) => setSearchForm(prev => ({ ...prev, send: val }))}
                                                />
                                            </div>
                                                {showSummary && trackingResult?.summary && (
                                                <TrackingSummaryBox
                                                    summary={trackingResult.summary}
                                                    filter={{
                                                        status: searchForm.status,
                                                        payKind: searchForm.payKind,
                                                        excludeCancel: searchForm.excludeCancel,
                                                    }}
                                                    onFilter={patch => handleSummaryFilter(patch)}
                                                    onReset={() =>
                                                        handleSummaryFilter({ status: 0, payKind: "", excludeCancel: false })
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="pt-4 flex items-center">
                                        <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">부서</p>
                                        <SelectBox
                                            className="w-80 bg-white"
                                            placeholder="부서명을 선택해주세요."
                                            value={searchForm.department}
                                            onChange={val => setSearchForm(prev => ({ ...prev, department: val }))}
                                            options={[
                                                { value: "all", label: "전체부서" },
                                                ...(deptData?.map(dept => ({
                                                    value: dept.sn.toString(),
                                                    label: dept.dept_name,
                                                })) || []),
                                            ]}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* 700 이상 */}
                        {!responsive700 && (
                            <div className="flex gap-2 responsive1200:mt-4 responsive700:hidden">
                                <Button size="L" onClick={handleReset}>
                                    검색 초기화
                                </Button>
                                <Button
                                    size="L"
                                    className="border-none bg-[#132e47] text-white"
                                    onClick={() => {
                                        if (searchForm.blNo.startsWith("1")) {
                                            navigate(`/customer/tracking/detail?bl_no=${searchForm.blNo}`);
                                        } else {
                                            setSearchForm(prev => {
                                                const newFilter: SearchFormProps = {
                                                    ...prev,
                                                    page: 1,
                                                    pageSize: 10,
                                                    // 새로 검색하면 요약 숫자 클릭 필터는 해제한다
                                                    payKind: "",
                                                    excludeCancel: false,
                                                };

                                                setShowSummary(!!(newFilter.startDate && newFilter.endDate));
                                                fetchTrackingList(newFilter);
                                                return newFilter;
                                            });
                                        }
                                    }}
                                >
                                    검색
                                </Button>
                            </div>
                        )}
                    </div>

                    <TrackingList
                        onClose={() => fetchTrackingList()}
                        data={trackingResult}
                        page={searchForm.page}
                        onChangePage={page => {
                            setSearchForm(prev => {
                                const newData = { ...prev, page };
                                fetchTrackingList(newData);
                                return newData;
                            });
                        }}
                        filter={searchFilter}
                        isApp={isApp}
                    />
                </div>
            </CustomerLayout>
        </React.Fragment>
    );
};

const MorningTracking = () => {
    // const [tokenReady, setTokenReady] = useState(false);
    // const [isApp, setIsApp] = useState(false); // 앱 여부
    const { isApp, tokenReady } = useAppContext();
    const accessToken = useMorningAuthStore(state => state.accessToken);

    // useEffect(() => {
    //     const handler = (event: MessageEvent) => {
    //         let msg = event.data;

    //         // 객체면 JSON 문자열로 변환
    //         if (typeof msg === "object") {
    //             try {
    //                 msg = JSON.stringify(msg);
    //             } catch (e) {
    //                 msg = String(msg);
    //             }
    //         }

    //         alert(`앱에서 받은 메시지: ${msg}`);
    //     };

    //     window.addEventListener("message", handler);
    //     return () => window.removeEventListener("message", handler);
    // }, []);

    // 1. 토큰 세팅 (앱 메시지 → 앱 토큰, 웹 → 기존 토큰)
    // useEffect(() => {
    //     const handleMessage = (event: MessageEvent) => {
    //         let token: string | undefined;

    //         if (typeof event.data === "string") {
    //         // 문자열이면 JSON 파싱 시도

    //             try {
    //                 const parsed = JSON.parse(event.data);
    //                 token = parsed.data?.accessToken;
    //             } catch {
    //                 // 문자열이지만 JSON이 아니면 무시
    //             }

    //         } else if (typeof event.data === "object" && event.data !== null) {
    //             // 이미 객체면 바로 접근
    //             token = (event.data as any).data?.accessToken;
    //         }

    //         if (token) {
    //             // 앱 토큰으로 로그인 스위칭
    //             useMorningAuthStore.getState().setAuth({
    //                 accessToken: token,
    //             });
    //         }

    //         setTokenReady(true) // 토큰 준비 완료

    //     };

    //     // 앱 메시지 리스너 등록
    //     window.addEventListener("message", handleMessage);

    //     // 웹 환경일 경우(앱 메시지 안 옴) → 기존 토큰 사용
    //     const existingToken = useMorningAuthStore.getState().accessToken;
    //     if (existingToken) {
    //         setTokenReady(true);
    //     }

    //     return () => {
    //         window.removeEventListener("message", handleMessage);
    //     };
    // }, []);

    // 1. 토큰 세팅 (앱 메시지 → 앱 토큰, 웹 → 기존 토큰)
    // useEffect(() => {
    //     const handleMessage = (event: MessageEvent) => {
    //         let payload: any;

    //         if (typeof event.data === "string") {
    //             try {
    //                 payload = JSON.parse(event.data);
    //             } catch {
    //                 return;
    //             }
    //         } else if (typeof event.data === "object" && event.data !== null) {
    //             payload = event.data;
    //         } else {
    //             return;
    //         }

    //         if (payload?.type === "SET_TOKENS") {
    //             const token = payload.data?.accessToken;
    //             const info = payload.data;

    //             // 앱 접근임
    //             setIsApp(true);

    //             if (token) {
    //                 useMorningAuthStore.getState().setAuth({
    //                     accessToken: token,
    //                     ...info,
    //                 });
    //             }

    //             setTokenReady(true);
    //         }
    //     };

    //     window.addEventListener("message", handleMessage);

    //     // 웹: 기존 토큰이 있으면 tokenReady만 true
    //     const existingToken = useMorningAuthStore.getState().accessToken;
    //     if (existingToken) {
    //         setTokenReady(true);
    //     }

    //     return () => {
    //         window.removeEventListener("message", handleMessage);
    //     };
    // }, []);

    const isDevDomain = useIsDevelopment(); // 개발 운영 모드
    const alertShown = useRef(false);
    useEffect(() => {
        if (isDevDomain && isApp && tokenReady && accessToken && !alertShown.current) {
            alertShown.current = true;
            alert("현재 저장된 토큰: " + accessToken);
        }
    }, [isApp, tokenReady, accessToken]);

    // 앱이면 tokenReady 될 때까지 Spinner, 웹이면 바로 Tracking
    if (isApp && !tokenReady && !accessToken) {
        return (
            <div className="min-h-[100dvh]">
                <Spinner />
            </div>
        );
    }

    return <Tracking token={accessToken} isApp={isApp} />;
};

export default MorningTracking;
